import { useEffect, useMemo, useState } from "react";
import { History, LogIn, RefreshCcw, Search, RotateCcw } from "lucide-react";
import { auditApi, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function classify(action = "") {
  const a = action.toLowerCase();
  if (a.includes("accesso")) return "accessi";
  if (a.includes("password")) return "sicurezza";
  if (a.includes("sincronizzata") || a.includes("ruolo") || a.includes("canale") || a.includes("categoria")) return "struttura";
  if (a.includes("messaggio") || a.includes("embed")) return "messaggi";
  if (a.includes("membro")) return "membri";
  if (a.includes("funzione")) return "funzioni";
  if (a.includes("backup")) return "backup";
  if (a.includes("impostazioni")) return "impostazioni";
  return "altro";
}

const CATS = [
  { value: "all", label: "Tutti i tipi" },
  { value: "accessi", label: "Accessi" },
  { value: "sicurezza", label: "Sicurezza" },
  { value: "struttura", label: "Struttura" },
  { value: "messaggi", label: "Messaggi" },
  { value: "membri", label: "Membri" },
  { value: "funzioni", label: "Funzioni automatiche" },
  { value: "backup", label: "Backup" },
  { value: "impostazioni", label: "Impostazioni" },
];

function iconFor(action) {
  if (action.includes("Accesso effettuato")) return { Icon: LogIn, color: "#23A55A" };
  if (action.includes("Accesso fallito")) return { Icon: LogIn, color: "#F23F43" };
  if (action.includes("Sincronizzata")) return { Icon: RefreshCcw, color: "#5865F2" };
  return { Icon: History, color: "#949BA4" };
}

export default function Audit() {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = async () => {
    try { setItems(await auditApi()); } catch (e) { setErr(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((it) => {
      if (cat !== "all" && classify(it.action) !== cat) return false;
      if (q && !(`${it.action} ${it.detail || ""}`.toLowerCase().includes(q))) return false;
      if (it.at) {
        const d = it.at.slice(0, 10);
        if (from && d < from) return false;
        if (to && d > to) return false;
      }
      return true;
    });
  }, [items, query, cat, from, to]);

  const resetFilters = () => { setQuery(""); setCat("all"); setFrom(""); setTo(""); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">Registro attività</h1>
          <p className="text-sm text-[#949BA4]">Storico delle azioni eseguite dalla dashboard.</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="audit-refresh" className="border-[#232838] bg-transparent text-[#B5BAC1] hover:bg-[#252B3D]">
          <RefreshCcw className="mr-2 h-4 w-4" />Aggiorna
        </Button>
      </div>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#949BA4]" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca nel registro…"
            data-testid="audit-search-input" className="border-[#232838] bg-[#131620] pl-9 text-white" />
        </div>
        <div className="w-full lg:w-52">
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger data-testid="audit-type-filter" className="border-[#232838] bg-[#131620] text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="border-[#232838] bg-[#161923] text-[#DBDEE1]">
              {CATS.map((c) => <SelectItem key={c.value} value={c.value} className="focus:bg-[#252B3D]">{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-[10px] font-bold uppercase text-[#949BA4]">Dal</label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="audit-date-from" className="border-[#232838] bg-[#131620] text-white" />
        </div>
        <div>
          <label className="mb-1 block text-[10px] font-bold uppercase text-[#949BA4]">Al</label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="audit-date-to" className="border-[#232838] bg-[#131620] text-white" />
        </div>
        {(query || cat !== "all" || from || to) && (
          <Button variant="ghost" onClick={resetFilters} data-testid="audit-reset-filters" className="text-[#B5BAC1]"><RotateCcw className="mr-2 h-4 w-4" />Azzera</Button>
        )}
      </div>

      {err && <div className="rounded-md bg-[#F23F43]/15 px-4 py-2 text-sm text-[#F23F43]">{err}</div>}

      <p className="text-xs text-[#949BA4]">{filtered.length} attività{filtered.length !== items.length ? ` (su ${items.length})` : ""}</p>

      <Card className="border-[#232838] bg-[#161923]" data-testid="audit-list">
        <div className="divide-y divide-[#232838]">
          {filtered.length === 0 && <div className="px-4 py-8 text-center text-sm text-[#949BA4]">Nessuna attività corrisponde ai filtri.</div>}
          {filtered.map((it, i) => {
            const { Icon, color } = iconFor(it.action);
            return (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: `${color}22` }}>
                  <Icon className="h-4 w-4" style={{ color }} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-white">{it.action}</p>
                  {it.detail && <p className="truncate text-xs text-[#949BA4]">{it.detail}</p>}
                </div>
                <div className="text-right">
                  <p className="text-xs text-[#949BA4]">{it.at ? new Date(it.at).toLocaleString("it-IT") : ""}</p>
                  <p className="text-[10px] text-[#5d6169]">{it.actor}{it.ip ? " · " + it.ip : ""}</p>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
